// Единый поток загрузки словаря фильтров: читаем кэш → показываем мгновенно → при нужде
// тихо обновляем из сети → пишем в кэш. Раньше этот двадцатистрочный танец был скопирован
// в четыре лисенера (project, category, extension, tag) — расходились бы они молча.
//
// Различия словарей вынесены в параметры: как достать элементы из ответа (у extension это
// объект, а не массив), какой экшен применяет их к своему слайсу и надо ли затирать список
// при неудачной первой загрузке.

import { message } from '@myua/kit';
import { Action } from 'redux';
import { isCacheStale } from '../../cache';
import { ListenerApi } from '../../listenerMiddleware';
import * as filtersCacheActions from './actions';
import { StoredPayload } from './actions';
import { readDictionaryCache } from './helpers';
import { DictionaryItem, DictionaryName } from './model';

export type LoadDictionaryOptions<N extends DictionaryName> = {
  dict: N;
  // Параметры, от которых зависит содержимое словаря — они же образуют ключ кэша.
  params: Record<string, unknown>;
  setLoading: (options: { loading: boolean }) => Action;
  itemsChanged: (items: DictionaryItem<N>[]) => Action;
  // Элементы словаря либо undefined, если запрос не удался.
  request: () => Promise<DictionaryItem<N>[] | undefined>;
  errorMessage: string;
  // Показывать пустой словарь, если первая загрузка не удалась. У проектов список при
  // ошибке не затирается — там пустой сайдбар выглядел бы как «нет доступа».
  clearOnError?: boolean;
};

// Если загрузку перебил более свежий запрос, выходит ничего не меняя — вызывающий лисенер
// обязан сам проверить signal.aborted и тоже прекратить работу (проверки консистентности
// выполнит та свежая загрузка).
export async function loadDictionary<N extends DictionaryName>(
  {
    dict,
    params,
    setLoading,
    itemsChanged,
    request,
    errorMessage,
    clearOnError = true,
  }: LoadDictionaryOptions<N>,
  { getState, dispatch, signal }: Pick<ListenerApi, 'getState' | 'dispatch' | 'signal'>,
): Promise<void> {
  const { key, entry } = readDictionaryCache(getState(), dict, params);

  if (entry) {
    // Этот набор фильтров уже загружался — показываем словарь мгновенно и без спиннера.
    // Флаг загрузки гасим обязательно: предыдущую загрузку мог прервать takeLatest уже
    // ПОСЛЕ setLoading(true), и тогда флаг остался бы висеть. Для пустого словаря
    // (`items: []`) это означало бы вечный скелетон — условие показа `loading && пусто`.
    dispatch(setLoading({ loading: false }));
    dispatch(itemsChanged(entry.items));
    if (!isCacheStale(entry)) {
      // Запись получена в этой сессии только что — в сеть не идём.
      return;
    }
    // Запись из прошлой сессии или пролежавшая слишком долго — обновляем в фоне.
  } else {
    dispatch(setLoading({ loading: true }));
  }

  const items = await request();
  if (signal.aborted) {
    return;
  }
  dispatch(setLoading({ loading: false }));

  if (items) {
    dispatch(itemsChanged(items));
    // Соответствие dict → тип элементов гарантировано параметром N; для переменной из
    // union TS этого не выводит, поэтому здесь единственное приведение на весь модуль.
    dispatch(filtersCacheActions.stored({ dict, key, items } as StoredPayload));
  } else if (!entry) {
    // При тихом ре-валидейте (данные из кэша уже на экране) ошибку не показываем: ни
    // тостом, ни деструктивной заменой на пустой список.
    void message.error(errorMessage);
    if (clearOnError) {
      dispatch(itemsChanged([]));
    }
  }
}
